import { ThemedText } from "@/components/ui";
import {
    Select,
    SelectItem,
    DatePicker,
    Checkbox,
    NumberInput,
} from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { Controller } from "react-hook-form";

const EmploymentStep = ({
    errors,
    control,
    watch,
    positions = [],
    departments = [],
    salaryTypes = [],
    isEdit = false,
}) => {
    const hasStopped = watch("has_stopped_working");

    return (
        <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Jabatan */}
                <Controller
                    name="position_id"
                    control={control}
                    rules={{ required: "Jabatan wajib dipilih" }}
                    render={({ field }) => (
                        <Select
                            label="Jabatan"
                            placeholder="Pilih jabatan"
                            selectedKeys={
                                field.value
                                    ? new Set([String(field.value)])
                                    : new Set()
                            }
                            onSelectionChange={(keys) =>
                                field.onChange(Array.from(keys)[0])
                            }
                            errorMessage={errors.position_id?.message}
                            isInvalid={!!errors.position_id}
                            variant="bordered"

                            isRequired
                        >
                            {positions.map((item) => (
                                <SelectItem
                                    key={String(item.value)}
                                    value={item.value}
                                >
                                    {item.title}
                                </SelectItem>
                            ))}
                        </Select>
                    )}
                />

                {/* Departemen */}
                <Controller
                    name="department_id"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Departemen"
                            placeholder="Pilih departemen"
                            selectedKeys={
                                field.value
                                    ? new Set([String(field.value)])
                                    : new Set()
                            }
                            onSelectionChange={(keys) =>
                                field.onChange(Array.from(keys)[0])
                            }
                            variant="bordered"

                        >
                            {departments.map((item) => (
                                <SelectItem
                                    key={String(item.value)}
                                    value={item.value}
                                >
                                    {item.title}
                                </SelectItem>
                            ))}
                        </Select>
                    )}
                />

                {/* NPWP */}
                <Controller
                    name="tax_identification_number"
                    control={control}
                    rules={{
                        validate: (value) =>
                            !value ||
                            String(value).length === 15 ||
                            "Nomor NPWP harus 15 digit",
                    }}
                    render={({ field }) => (
                        <NumberInput
                            label="Nomor NPWP"
                            placeholder="Masukkan nomor NPWP"
                            value={field.value}
                            onValueChange={field.onChange}
                            errorMessage={
                                errors?.tax_identification_number?.message
                            }
                            isInvalid={!!errors?.tax_identification_number}
                            maxLength={15}
                            hideStepper
                            formatOptions={{ useGrouping: false }}
                            variant="bordered"

                        />
                    )}
                />

                {/* Checkbox Berhenti Bekerja - Only show on Edit */}
                {isEdit && (
                    <div className="md:col-span-2">
                        <Controller
                            name="has_stopped_working"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    isSelected={field.value}
                                    onValueChange={field.onChange}
                                    classNames={{
                                        label: "text-sm",
                                    }}
                                >
                                    Staff sudah berhenti bekerja
                                </Checkbox>
                            )}
                        />
                    </div>
                )}

                {/* Tanggal Mulai Bekerja */}
                <Controller
                    name="start_date"
                    control={control}
                    rules={{ required: "Tanggal mulai wajib diisi" }}
                    render={({ field }) => (
                        <DatePicker
                            label="Tanggal Mulai Bekerja"
                            value={field.value ? parseDate(field.value) : null}
                            onChange={(date) =>
                                field.onChange(date ? date.toString() : "")
                            }
                            errorMessage={errors.start_date?.message}
                            isInvalid={!!errors.start_date}
                            variant="bordered"

                            showMonthAndYearPickers
                            isRequired
                        />
                    )}
                />

                {/* Tanggal Berhenti Bekerja (Conditional) */}
                {hasStopped && (
                    <Controller
                        name="end_date"
                        control={control}
                        rules={{
                            required:
                                "Tanggal berhenti wajib diisi jika tidak aktif",
                        }}
                        render={({ field }) => (
                            <DatePicker
                                label="Tanggal Berhenti"
                                value={
                                    field.value ? parseDate(field.value) : null
                                }
                                onChange={(date) =>
                                    field.onChange(date ? date.toString() : "")
                                }
                                errorMessage={errors.end_date?.message}
                                isInvalid={!!errors.end_date}
                                variant="bordered"
    
                                showMonthAndYearPickers
                                isRequired
                            />
                        )}
                    />
                )}

                {/* Jenis Gaji */}
                <Controller
                    name="salary_type_id"
                    control={control}
                    rules={{ required: "Jenis gaji wajib dipilih" }}
                    render={({ field }) => (
                        <Select
                            label="Jenis Gaji"
                            placeholder="Pilih jenis gaji"
                            selectedKeys={
                                field.value
                                    ? new Set([String(field.value)])
                                    : new Set()
                            }
                            onSelectionChange={(keys) =>
                                field.onChange(Array.from(keys)[0])
                            }
                            errorMessage={errors.salary_type_id?.message}
                            isInvalid={!!errors.salary_type_id}
                            variant="bordered"

                            isRequired
                        >
                            {salaryTypes.map((item) => (
                                <SelectItem
                                    key={String(item.value)}
                                    value={item.value}
                                >
                                    {item.title}
                                </SelectItem>
                            ))}
                        </Select>
                    )}
                />

                {/* Nominal Gaji */}
                <Controller
                    name="salary"
                    control={control}
                    rules={{ required: "Gaji wajib diisi" }}
                    render={({ field }) => (
                        <NumberInput
                            label="Nominal Gaji"
                            placeholder="0"
                            value={field.value !== "" ? field.value : undefined}
                            onValueChange={field.onChange}
                            startContent={
                                <ThemedText size="sm" variant="secondary">
                                    Rp
                                </ThemedText>
                            }
                            formatOptions={{
                                style: "decimal",
                                maximumFractionDigits: 0,
                            }}
                            errorMessage={errors.salary?.message}
                            isInvalid={!!errors.salary}
                            variant="bordered"

                            hideStepper
                            minValue={0}
                            isRequired
                        />
                    )}
                />
            </div>
        </div>
    );
};

export default EmploymentStep;
